import { axisClasses, BarChart } from "@mui/x-charts";
import { AuthorAnnualMetrics } from "../CommonInterfaces";
import { useMemo } from "react";

interface AuthorAnnualMetricsBarChartProps {
  data: AuthorAnnualMetrics[];
}

const yearValueFormatter = (value: number | null) => `${value}`;

const chartSetting = {
  yAxis: [
    {
      label: "Number of articles",
    },
  ],
  height: 150,
  sx: {
    [`.${axisClasses.left} .${axisClasses.label}`]: {
      transform: "translate(-10px, 0)",
    },
    ml: 10,
    mr: 10,
  },
};

function AuthorAnnualMetricsBarChart({ data }: AuthorAnnualMetricsBarChartProps) {
  // Create a map of actual data
  const dataMap = useMemo(() => {
    const map = new Map<number, AuthorAnnualMetrics>();
    data.forEach((entry) => {
      map.set(entry.year, entry);
    });
    return map;
  }, [data]);

  // Generate full year range
  const startYear = 2014;
  const endYear = new Date().getFullYear();
  const allYears = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  // Ensure all years are represented in the dataset
  const annualMetrics = allYears.map((year) => {
    const entry = dataMap.get(year);
    const analysed = entry?.analysed ?? 0;
    const highRisk = entry?.highRisk ?? 0;
    const mediumRisk = entry?.mediumRisk ?? 0;
    const retractions = entry?.retractions ?? 0;
    const lowRisk = analysed - (highRisk + mediumRisk + retractions);

    return {
      year,
      retractions,
      lowRisk,
      mediumRisk,
      highRisk,
    };
  });

  return (
    <BarChart
      dataset={annualMetrics}
      xAxis={[
        {
          label: "Publication Year",
          scaleType: "band",
          dataKey: "year",
          valueFormatter: yearValueFormatter,
        },
      ]}
      series={[
        { dataKey: "lowRisk", stack: "history", label: "Low Risk Articles", color: "#1565C0" },
        { dataKey: "mediumRisk", stack: "history", label: "Medium Risk Articles", color: "#ff6700" },
        { dataKey: "highRisk", stack: "history", label: "High Risk Articles", color: "red" },
        { dataKey: "retractions", stack: "history", label: "Retractions (by publication year)", color: "#8B0000" },
      ]}
      legend={{ hidden: true }}
      {...chartSetting}
    />
  );
}

export default AuthorAnnualMetricsBarChart;
